import { useCallback, useEffect, useState } from "react";
import { Plus, Trash2, Search, Landmark, Copy } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg, fmtRp, fmtDate, todayISO, COMPANY } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader } from "@/components/StatCard";
import { BankMutations, MootaSettings } from "@/components/BankMutations";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { MoneyInput } from "@/components/ui/money-input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";

const empty = { bank: "BCA", amount: "", sender: "", date: todayISO(), reference: "", note: "", apply_to: "receivable", transaction_id: "" };
const NONE = "__none__";

export default function Payments() {
  const { can } = useAuth(); const isAdmin = can("payments_manage");
  const [list, setList] = useState([]);
  const [sales, setSales] = useState([]);
  const [filters, setFilters] = useState({ search: "", bank: "all" });
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = useCallback(() => {
    api.get("/payments", { params: { ...(filters.search && { search: filters.search }), ...(filters.bank !== "all" && { bank: filters.bank }) } }).then((r) => setList(r.data));
  }, [filters]);
  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [load]);
  useEffect(() => { api.get("/transactions", { params: { type: "out", limit: 100 } }).then((r) => setSales(r.data.filter((t) => t.total_value))); }, []);

  const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));
  const pickSale = (id) => {
    const s = sales.find((t) => t.id === id);
    setForm((p) => ({ ...p, transaction_id: id === NONE ? "" : id, ...(s && { amount: s.total_value, sender: s.destination || p.sender, reference: s.receipt_no }) }));
  };

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post("/payments", { ...form, amount: Number(form.amount), transaction_id: form.transaction_id || null });
      toast.success("Pembayaran masuk dicatat"); setForm(null); load();
    } catch (err) { toast.error(errMsg(err)); } finally { setSaving(false); }
  };

  const remove = async (p) => {
    if (!window.confirm(`Hapus pembayaran ${fmtRp(p.amount)} dari ${p.sender}?`)) return;
    try { await api.delete(`/payments/${p.id}`); toast.success("Pembayaran dihapus"); load(); } catch (e) { toast.error(errMsg(e)); }
  };

  const copy = (t) => { navigator.clipboard?.writeText(t); toast.success("Nomor rekening disalin"); };
  const total = list.reduce((a, p) => a + p.amount, 0);

  return (
    <div data-testid="payments-page">
      <PageHeader title="Pembayaran Masuk" subtitle="Catat dana yang masuk ke rekening perusahaan — notifikasi & jurnal dibuat otomatis">
        <Button className="rounded-full bg-sky-600 hover:bg-sky-700" onClick={() => setForm(empty)} data-testid="btn-add-payment"><Plus className="h-4 w-4 mr-1" /> Catat Pembayaran</Button>
      </PageHeader>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
        {COMPANY.banks.map((b, i) => (
          <div key={b.bank} className={`rounded-2xl p-5 text-white rise rise-${i + 1} ${b.bank === "BCA" ? "bg-gradient-to-br from-blue-700 to-sky-600" : "bg-gradient-to-br from-blue-900 to-indigo-700"}`} data-testid={`bank-card-${b.bank}`}>
            <div className="flex items-center justify-between"><span className="inline-flex items-center gap-2 font-extrabold tracking-wider"><Landmark className="h-4 w-4" /> Bank {b.bank}</span><button onClick={() => copy(b.account_no)} className="p-1.5 rounded-lg bg-white/15 hover:bg-white/25" data-testid={`btn-copy-${b.bank}`}><Copy className="h-3.5 w-3.5" /></button></div>
            <div className="mt-4 font-mono text-2xl font-bold tracking-[0.12em]">{b.account_no}</div>
            <div className="text-xs text-white/80 mt-1">a.n. {b.holder}</div>
          </div>
        ))}
        <div className="card-aqua p-5 rise rise-3">
          <div className="text-xs font-semibold uppercase tracking-wider text-slate-500">Total masuk (filter)</div>
          <div className="mt-3 text-2xl font-extrabold font-mono text-emerald-600" data-testid="payments-total">{fmtRp(total)}</div>
          <div className="text-xs text-slate-500 mt-1">{list.length} pembayaran</div>
        </div>
      </div>

      <div className="card-aqua p-4 mb-4 flex flex-col md:flex-row gap-3 rise rise-2">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
          <Input placeholder="Cari pengirim, referensi..." value={filters.search} onChange={(e) => setFilters({ ...filters, search: e.target.value })} className="pl-9" data-testid="input-search-payments" />
        </div>
        <Select value={filters.bank} onValueChange={(v) => setFilters({ ...filters, bank: v })}>
          <SelectTrigger className="md:w-44" data-testid="select-filter-bank"><SelectValue /></SelectTrigger>
          <SelectContent><SelectItem value="all">Semua bank</SelectItem><SelectItem value="BCA">BCA</SelectItem><SelectItem value="BRI">BRI</SelectItem></SelectContent>
        </Select>
      </div>

      <div className="card-aqua overflow-hidden rise rise-3">
        <table className="w-full text-sm" data-testid="table-payments">
          <thead className="table-head"><tr><th className="text-left px-4 py-3">Tanggal</th><th className="text-left px-4 py-3">Bank</th><th className="text-left px-4 py-3">Pengirim</th><th className="text-left px-4 py-3">Referensi</th><th className="text-left px-4 py-3">Alokasi</th><th className="text-right px-4 py-3">Nominal</th><th className="text-left px-4 py-3">Dicatat oleh</th><th className="px-4 py-3" /></tr></thead>
          <tbody>
            {list.length === 0 && <tr><td colSpan={8} className="text-center py-12 text-slate-500" data-testid="payments-empty">Belum ada pembayaran masuk.</td></tr>}
            {list.map((p, idx) => (
              <tr key={p.id} className="border-t border-sky-50 hover:bg-sky-50/50" data-testid={`payment-row-${idx}`}>
                <td className="px-4 py-3 text-slate-600 whitespace-nowrap">{fmtDate(p.date)}</td>
                <td className="px-4 py-3"><span className={`px-2.5 py-0.5 rounded-full text-xs font-bold text-white ${p.bank === "BCA" ? "bg-blue-600" : "bg-indigo-700"}`}>{p.bank}</span></td>
                <td className="px-4 py-3 font-semibold text-slate-800">{p.sender}{p.note && <div className="text-xs text-slate-400 font-normal">{p.note}</div>}</td>
                <td className="px-4 py-3 font-mono text-xs text-slate-500">{p.reference || "—"}</td>
                <td className="px-4 py-3 text-xs text-slate-600">{p.apply_to === "receivable" ? "Pelunasan piutang" : "Pendapatan penjualan"}</td>
                <td className="px-4 py-3 text-right font-mono font-bold text-emerald-600">+{fmtRp(p.amount)}</td>
                <td className="px-4 py-3 text-slate-600">{p.created_by}</td>
                <td className="px-4 py-3 text-right">{isAdmin && <Button variant="ghost" size="icon" className="h-8 w-8 text-rose-600" onClick={() => remove(p)} data-testid={`btn-delete-payment-${idx}`}><Trash2 className="h-4 w-4" /></Button>}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="mt-6 rise rise-3"><BankMutations isAdmin={isAdmin} onConfirmed={load} /></div>
      {isAdmin && <div className="mt-6 rise rise-4"><MootaSettings /></div>}

      <Dialog open={!!form} onOpenChange={(o) => !o && setForm(null)}>
        <DialogContent className="sm:max-w-lg rounded-2xl" data-testid="payment-form-dialog">
          <DialogHeader><DialogTitle>Catat Pembayaran Masuk</DialogTitle><DialogDescription>Dana yang diterima di rekening BCA / BRI perusahaan.</DialogDescription></DialogHeader>
          {form && (
            <form onSubmit={submit} className="space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label>Rekening Tujuan</Label>
                  <Select value={form.bank} onValueChange={(v) => set("bank", v)}>
                    <SelectTrigger data-testid="select-payment-bank"><SelectValue /></SelectTrigger>
                    <SelectContent>{COMPANY.banks.map((b) => <SelectItem key={b.bank} value={b.bank}>{b.bank} • {b.account_no}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div className="space-y-1.5"><Label>Tanggal</Label><Input type="date" value={form.date} onChange={(e) => set("date", e.target.value)} required data-testid="input-payment-date" /></div>
              </div>
              <div className="space-y-1.5">
                <Label>Terkait Penjualan (opsional)</Label>
                <Select value={form.transaction_id || NONE} onValueChange={pickSale}>
                  <SelectTrigger data-testid="select-payment-sale"><SelectValue placeholder="Pilih nota penjualan" /></SelectTrigger>
                  <SelectContent className="max-h-64"><SelectItem value={NONE}>Tidak terkait nota</SelectItem>{sales.map((s) => <SelectItem key={s.id} value={s.id}>{s.receipt_no} — {s.destination || s.species_name} ({fmtRp(s.total_value)})</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5"><Label>Nominal (Rp)</Label><MoneyInput value={form.amount} onChange={(v) => set("amount", v)} required data-testid="input-payment-amount" /></div>
                <div className="space-y-1.5"><Label>Pengirim</Label><Input value={form.sender} onChange={(e) => set("sender", e.target.value)} required placeholder="cth. PT Mina Ekspor" data-testid="input-payment-sender" /></div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5"><Label>Referensi / Berita</Label><Input value={form.reference} onChange={(e) => set("reference", e.target.value)} placeholder="No. nota / berita transfer" data-testid="input-payment-reference" /></div>
                <div className="space-y-1.5">
                  <Label>Alokasi Jurnal</Label>
                  <Select value={form.apply_to} onValueChange={(v) => set("apply_to", v)}>
                    <SelectTrigger data-testid="select-payment-apply"><SelectValue /></SelectTrigger>
                    <SelectContent><SelectItem value="receivable">Pelunasan piutang usaha</SelectItem><SelectItem value="revenue">Pendapatan penjualan langsung</SelectItem></SelectContent>
                  </Select>
                </div>
              </div>
              <div className="space-y-1.5"><Label>Catatan</Label><Input value={form.note} onChange={(e) => set("note", e.target.value)} placeholder="Opsional" data-testid="input-payment-note" /></div>
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setForm(null)}>Batal</Button>
                <Button type="submit" disabled={saving || !form.amount || !form.sender} className="bg-sky-600 hover:bg-sky-700" data-testid="btn-payment-submit">{saving ? "Menyimpan..." : "Simpan & Notifikasi"}</Button>
              </DialogFooter>
            </form>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
